/**
 * Tiny localStorage cache for expensive lookups. Keeps the time-to-
 * recommendation short on revisits.
 *
 * - geocode: city → {lat, lng}, immortal (cities don't move).
 * - forecast: stable key → data, 10-minute TTL (weather changes slowly
 *   relative to how often someone refreshes a page).
 */

const safeGet = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* quota / disabled */ }
};

// --- Geocode (immortal) ---

const GEOCODE_PREFIX = 'layers-today:geocode:';

const normalizeCity = (city) => (city || '').trim().toLowerCase();

export const getCachedGeocode = (city) => {
  const key = GEOCODE_PREFIX + normalizeCity(city);
  const raw = safeGet(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.latitude === 'number' && typeof parsed.longitude === 'number') return parsed;
  } catch { /* ignore */ }
  return null;
};

export const setCachedGeocode = (city, { latitude, longitude }) => {
  if (!city || typeof latitude !== 'number' || typeof longitude !== 'number') return;
  safeSet(GEOCODE_PREFIX + normalizeCity(city), JSON.stringify({ latitude, longitude }));
};

// --- Forecast (10-min TTL) ---

// v1 — bump when the cached response shape changes so old entries get
// invalidated wholesale instead of corrupting a render. Cheap insurance.
const FORECAST_PREFIX = 'layers-today:forecast:v1:';
const FORECAST_TTL_MS = 10 * 60 * 1000;

/** Round coords to 2 decimals (~1km) so nearby fetches hit the cache. */
const roundCoord = (n) => Math.round(n * 100) / 100;

/**
 * Build a stable key. `forecastHours` is meaningful for today mode; for
 * trip mode it's not in the URL and we use start/end dates instead — the
 * `extra` param lets the caller pass any additional discriminator.
 */
export const buildForecastKey = ({ latitude, longitude, forecastHours = 0, mode = 'today', extra = '' }) =>
  `${FORECAST_PREFIX}${mode}:${roundCoord(latitude)},${roundCoord(longitude)}:${forecastHours}${extra ? ':' + extra : ''}`;

export const getCachedForecast = (key) => {
  const raw = safeGet(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number' || !parsed.data) return null;
    if (Date.now() - parsed.ts > FORECAST_TTL_MS) return null;
    return parsed.data;
  } catch { return null; }
};

export const setCachedForecast = (key, data) => {
  safeSet(key, JSON.stringify({ ts: Date.now(), data }));
};
